import { Button, Stack, Typography } from '@mui/material';
import type { FC } from 'react';
import React, { useCallback, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import { useSnackbar } from 'notistack';
import { spAppMainPages } from '@pflegenavi/frontend/routing';
import { LoadingPromiseButtonAdvanced } from '@pflegenavi/web-components';
import { MangopayConnectFormStep3 } from './MangopayConnectForm/MangopayConnectFormStep3';
import type {
  RecipientData,
  RecipientSchemaParams,
  CreateRecipientRequest,
  RecipientCreationResponse,
} from '@pflegenavi/service-provider-frontend/api';

interface MangopayRecipientFormProps {
  countryOptions: Array<{
    label: string;
    value: string;
  }>;
  loading: boolean;
  handleCreateRecipient: (
    request: CreateRecipientRequest
  ) => Promise<RecipientCreationResponse>;
  handleSkipBankAccount: () => Promise<void>;
  accountAffiliationName?: string;
  legalPersonType?: 'SOLETRADER' | 'BUSINESS' | 'ORGANIZATION' | 'PARTNERSHIP';
  serviceProviderCountryId?: string;
}

export const MangopayRecipientForm: FC<MangopayRecipientFormProps> = ({
  countryOptions,
  loading,
  handleCreateRecipient,
  handleSkipBankAccount,
  accountAffiliationName,
  legalPersonType,
  serviceProviderCountryId,
}) => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { enqueueSnackbar } = useSnackbar();

  // SOLETRADER uses Individual recipient type, others (BUSINESS/ORGANIZATION/PARTNERSHIP) use Business
  // If legalPersonType is undefined, default to Business as a safe fallback
  const recipientType =
    legalPersonType === 'SOLETRADER' ? 'Individual' : 'Business';

  const [recipientSchemaParams, setRecipientSchemaParams] =
    useState<RecipientSchemaParams | null>(null);
  const [recipientData, setRecipientData] = useState<RecipientData>({
    DisplayName: '',
    PayoutMethodType: 'LocalBankTransfer',
    RecipientType: recipientType,
    Currency: 'EUR',
    Country: '', // Will be set by the child component based on schema
  });
  const [isFormValid, setIsFormValid] = useState(false);

  const handleSubmit = useCallback(async () => {
    if (!recipientSchemaParams || !recipientData) {
      return;
    }

    const result = await handleCreateRecipient({
      ...recipientSchemaParams,
      recipient_data: recipientData,
    });

    // Handle SCA redirect if present
    // Note: The parent component (SetupBankAccountPage) is responsible for adding returnUrl
    const redirectUrl = result?.data?.redirect_url;
    if (redirectUrl) {
      enqueueSnackbar(
        t('service-provider-app.connect-account.redirecting-sca'),
        {
          variant: 'info',
        }
      );

      setTimeout(() => {
        window.location.href = redirectUrl;
      }, 500);
      return;
    }

    navigate(spAppMainPages.DASHBOARD);
  }, [
    handleCreateRecipient,
    recipientSchemaParams,
    recipientData,
    navigate,
    enqueueSnackbar,
    t,
  ]);

  const handleBack = useCallback(async () => {
    await handleSkipBankAccount();
  }, [handleSkipBankAccount]);

  const disabled = loading;

  return (
    <Stack alignItems="center" pt={4}>
      <Stack maxWidth={700} gap={3}>
        <Stack gap={0.5}>
          <Typography variant="h5" color="primary.main">
            {t('service-provider-app.connect-account.setup-bank-account')}
          </Typography>
          <Typography variant="subtitle2" color="grey.500">
            {t(
              'service-provider-app.connect-account.setup-bank-account-description'
            )}
          </Typography>
        </Stack>

        <MangopayConnectFormStep3
          disabled={disabled}
          onSchemaLoaded={setRecipientSchemaParams}
          onDataChange={setRecipientData}
          onValidationChange={setIsFormValid}
          recipientType={recipientType}
          serviceProviderCountryId={serviceProviderCountryId}
        />

        <Stack direction="row" justifyContent="center" gap={1}>
          <Button
            variant="outlined"
            size="large"
            onClick={handleBack}
            disabled={loading}
            fullWidth
          >
            {t('service-provider-app.connect-account.setup-later')}
          </Button>
          <LoadingPromiseButtonAdvanced
            variant="contained"
            size="large"
            disabled={!isFormValid || disabled}
            onClick={handleSubmit}
            fullWidth
          >
            {t('service-provider-app.connect-account.continue')}
          </LoadingPromiseButtonAdvanced>
        </Stack>

        {accountAffiliationName && (
          <Stack direction="row" justifyContent="center" gap={0.5}>
            <Typography variant="subtitle2" color="grey.500">
              {t('service-provider-app.connect-account.account-affiliation')}:
            </Typography>
            <Typography
              variant="subtitle2"
              color="primary.main"
              fontWeight={700}
            >
              {accountAffiliationName}
            </Typography>
          </Stack>
        )}
      </Stack>
    </Stack>
  );
};
